import { createContext, useContext, useEffect, useState } from 'react'

import { supabase } from '@/lib/supabase'
import { setSentryContext } from '@/lib/sentry'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import clsx from 'clsx'
import Bot from '@/pages/order/Bot'

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"

const AuthContext = createContext(null)

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)

  const [showBot, setShowBot] = useState(false)
  const toggleBot = () => setShowBot(!showBot)

  const setup = async (session) => {
    if (session) {
      setSession(session)
      setLoading(false)

      const { data: profile } = await supabase.from('profiles').select('*').eq('user_id', session?.user.id).single()

      if (profile) {
        const { data: settings } = await supabase.from('profile_settings').select('*').eq('profile_id', profile.id).single()
        const { data: transcriber } = await supabase.from('transcribers').select('*').eq('profile_id', profile.id).single()

        if (!transcriber || !transcriber.enable || transcriber.deleted_at || !settings) {
          return false
        }

        setSentryContext({ email: session.user.email, id: session.user.id })

        setUser({ profile, transcriber, settings: { ...settings, isAdmin: settings.permissions['read-folders'] } })


        supabase.from('profiles').update({
          last_sign_in_at: new Date().toISOString(),
        }).eq('user_id', session?.user.id).then()
      }
    }
  }

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session) {
        setLoading(false)
      }
    }

    getSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!!session) {
        setup(session)
      } else {
        setSession(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signOut = () => supabase.auth.signOut()

  return (
    <AuthContext.Provider value={{ user, session, signOut, setUser, toggleBot, showBot }}>
      {loading && <div className="h-screen w-screen bg-base"></div>}

      {!loading && <>
        {children}
      </>}
    </AuthContext.Provider >
  )
}